<template>
	<v-card class="w-100 mb-5 mt-md-0 mt-5" max-width="600" style="transition: all 0.3s ease">
		<v-card-title class="py-5 d-flex align-center text-md-h6" style="font-size: 16px">
			<v-icon class="mr-3">{{ tool.icon }}</v-icon>
			<span class="font-weight-regular" style="white-space: wrap; line-height: 1.24">
				{{ tool.new_document_title }}
			</span>
		</v-card-title>

		<v-divider />

		<v-tabs v-model="currentStep" density="compact" grow hide-slider class="border-b mb-2">
			<v-tab
				class="normal-spacing font-weight-light w-50 small-text tool-tab"
				:value="0"
				:class="currentStep === 0 ? 'active-tab' : ''">
				Context
			</v-tab>
			<v-divider vertical />
			<v-tab
				class="normal-spacing font-weight-light w-50 small-text tool-tab"
				:value="1"
				:class="currentStep === 1 ? 'active-tab' : ''">
				Business&nbsp;Information
			</v-tab>
		</v-tabs>

		<v-card-text class="py-5 px-5">
			<div v-if="currentStep === 0">
				<template v-if="tool.questions?.length">
					<div
						v-for="(question, index) in tool.questions"
						:key="index"
						class="w-100 flex-start flex-column mb-4">
						<span class="w-100 text-left font-weight-regular mb-1">{{ question.question }}</span>

						<v-text-field
							v-if="question.input_type === 'text'"
							:label="question.label"
							:placeholder="question.placeholder"
							v-model="formData[index]"
							hide-details
							class="w-100" />
						<v-select
							v-else-if="question.input_type === 'select'"
							:label="question.label"
							v-model="formData[index]"
							:items="JSON.parse(question.items)"
							hide-details
							class="w-100" />
						<v-textarea
							v-else-if="question.input_type === 'textarea'"
							:placeholder="question.label"
							v-model="formData[index]"
							hide-details
							class="w-100" />
						<v-checkbox
							v-else-if="question.input_type === 'checkbox'"
							:label="question.label"
							v-model="formData[index]"
							class="w-100" />
					</div>
				</template>
				<p v-else class="text-left w-100 text-disabled">No questions found.</p>

				<v-btn class="text-field-bg normal-spacing align-self-end mt-5 mb-1 w-100" @click="nextStep">
					Continue
				</v-btn>

				<div class="flex-center mt-3">
					<span class="px-3 text-disabled small-text">Free usages left today: {{ leftToday }}</span>
				</div>
			</div>

			<div v-else>
				<label class="w-100 text-left font-weight-regular" for="business_description">
					In a few words — what's your business about?
				</label>
				<v-textarea
					v-model="formData.business_description"
					id="business_description"
					label="Business description"
					placeholder="Example: We sell handmade candles online."
					hide-details
					class="w-100 mb-4 mt-1"
					rows="4" />

				<label class="w-100 text-left font-weight-regular" for="business_name">
					What is the name of your business?
				</label>
				<v-text-field
					v-model="formData.business_name"
					id="business_name"
					label="Business name"
					placeholder="e.g. Happy Cakes"
					class="w-100" />

				<div class="d-flex justify-space-between mt-5 mb-1">
					<v-btn variant="text" class="normal-spacing" color="#21212190" @click="prevStep">Back</v-btn>
					<v-btn class="normal-spacing text-field-bg" @click="submitForm">Generate Now</v-btn>
				</div>

				<div class="mt-3">
					<span class="px-3 text-disabled small-text">Free usages left today: {{ leftToday }}</span>
				</div>
			</div>
		</v-card-text>
	</v-card>

	<div class="w-100 flex-center flex-column">
		<v-alert
			v-if="errorMessage"
			style="max-width: 600px"
			closable
			type="error"
			density="compact"
			variant="tonal"
			class="w-100 mb-3"
			@click:close="errorMessage = ''">
			{{ errorMessage }}
		</v-alert>

		<v-alert density="compact" color="info" variant="text" class="w-100 text-center px-md-7 px-0">
			<span class="white--text">Get smarter, faster, more personalized content&nbsp;</span>
			<br class="hidden-sm-and-down" />
			<a href="{{ config('app.frontend_url') }}/new-business" class="text-decoration-underline blue-text">
				with the full experience.
			</a>
		</v-alert>
	</div>

	<v-dialog v-model="dialog" persistent max-width="700" scrim opacity=".8">
		<template v-if="loading">
			<div class="w-100 flex-center pa-10">
				<v-progress-circular indeterminate size="38" color="#ffffff" />
			</div>
		</template>
		<template v-else>
			<v-card>
				<v-card-title class="text-h6">Your Draft</v-card-title>
				<v-divider />
				<v-card-text style="white-space: pre-line">{{ toolOutput }}</v-card-text>
				<v-divider />
				<v-card-actions>
					<span class="px-3 text-disabled small-text">Free usages left today: {{ leftToday }}</span>
					<v-spacer />
					<v-btn class="normal-spacing" @click="dialog = false">Close</v-btn>
				</v-card-actions>
			</v-card>
		</template>
	</v-dialog>
</template>

<script setup>
	import axios from "axios"
	import { reactive, ref, onMounted, computed } from "vue"
	import { MAX_FREE, getCount, increment, remaining } from "@/utils/freeUsage"

	const props = defineProps({ tool: Object, ctaLink: String })

	const formData = reactive({ business_name: "", business_description: "" })
	const currentStep = ref(0)
	const dialog = ref(false)
	const loading = ref(false)
	const toolOutput = ref("")
	const errorMessage = ref("")

	// reactive mirror for the template
	const leftToday = ref(remaining())

	onMounted(() => {
		props.tool.questions?.forEach((q, idx) => {
			formData[idx] = q.input_type === "checkbox" ? false : ""
		})
	})

	const nextStep = () => (currentStep.value = 1)
	const prevStep = () => (currentStep.value = 0)

	async function submitForm() {
		if (leftToday.value <= 0) {
			errorMessage.value = "You've hit today's free limit. Sign up for unlimited usage."
			return
		}

		const qna = props.tool.questions.map((q, idx) => ({
			question: q.question,
			answer: formData[idx] ?? "",
		}))

		loading.value = true
		dialog.value = true
		errorMessage.value = ""

		try {
			const { data } = await axios.post("/quick-tool", {
				tool_id: props.tool.id,
				qna,
				business_name: formData.business_name,
				business_description: formData.business_description,
			})

			if (data?.output) {
				toolOutput.value = data.output
				loading.value = false

				increment()
				leftToday.value = remaining()
			} else {
				throw new Error()
			}
		} catch {
			dialog.value = false
			errorMessage.value = "Generation failed. Please try again later."
		}
	}
</script>

<style scoped>
	.active-tab {
		background-color: #f0f0f0;
		color: #000;
	}
	.tool-tab {
		font-size: 12px !important;
	}
</style>
